import { faInfoCircle } from '@fortawesome/pro-regular-svg-icons';
import { faSpinnerThird } from '@fortawesome/pro-regular-svg-icons/faSpinnerThird';
import * as React from 'react';
import { useTranslation } from 'react-i18next';

import Icon, { withIcon } from '../../core/components/Icon';
import Tooltip from '../../core/components/Tooltip.tsx';
import classnames from '../../core/utils/classnames.tsx';

export enum ChartCardMode {
    ACCUMULATED = 'accumulated',
    OVER_TIME = 'overTime',
}

interface ChartCardProps {
    title: string;
    helpText?: string;
    loading?: boolean;
    error?: boolean;
    className?: string;
    children?: React.ReactNode;
    note?: React.ReactNode;
    controls?: React.ReactNode;
}

const SpinnerIcon = withIcon(faSpinnerThird);

const ChartCard = ({ title, helpText, loading, error, note, className, controls, children }: ChartCardProps) => {
    const { t } = useTranslation('analytics');

    const childrenCount = React.Children.toArray(children).length;
    const hasChildren = childrenCount > 0;

    let displayChildren;
    if (loading) {
        displayChildren = (
            <div className="w-full h-full flex justify-center items-center">
                <SpinnerIcon spin className="text-3xl text-content-weak" />
            </div>
        );
    } else if (error) {
        displayChildren = (
            <div className="w-full h-full flex justify-center items-center">
                <span className="text-sm text-content-medium text-center">{t('dashboard.charts.error')}</span>
            </div>
        );
    } else if (!hasChildren) {
        displayChildren = (
            <div className="w-full h-full flex justify-center items-center">
                <span className="text-sm text-content-medium text-center">{t('dashboard.charts.noData')}</span>
            </div>
        );
    } else {
        displayChildren = children;
    }

    return (
        <div
            className={classnames(
                'shadow-sm/5 border border-line-weak bg-surface-weak p-7 flex flex-col gap-7 rounded-md flex-1 min-w-0 text-content-strong',
                className
            )}
        >
            <h3 className="flex gap-2 items-center">
                <span className="text-content-strong font-medium text-sm">{title}</span>
                {helpText ? (
                    <Tooltip content={helpText} className="text-center" side="top">
                        <Icon icon={faInfoCircle} className="text-content-medium mt-0.25 cursor-pointer" />
                    </Tooltip>
                ) : null}

                {controls && <div className="ml-auto">{controls}</div>}
            </h3>
            <div className="flex flex-1">{displayChildren}</div>
            {note ? <div className="text-xs text-content-medium text-center">{note}</div> : null}
        </div>
    );
};

export default ChartCard;
